import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtRM, fmtDate } from "../../lib/format";
import { useLang } from "../../lib/i18n";
import { PageTitle, StatusBadge, Spinner } from "../../components/Shell";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const TABS = [
  { key: "shares", bm: "Saham", en: "Shares" },
  { key: "fees", bm: "Yuran", en: "Fees" },
  { key: "payments", bm: "Bayaran & Resit", en: "Payments & Receipts" },
];

export default function Finance() {
  const [tab, setTab] = useState("shares");
  const [shares, setShares] = useState(null);
  const [fees, setFees] = useState(null);
  const [payments, setPayments] = useState(null);
  const [members, setMembers] = useState([]);
  const [modal, setModal] = useState(null); // 'share' | 'fee' | 'payment'
  const [form, setForm] = useState({ member_id: "", type: "purchase", amount: "", note: "", fee_type: "Yuran Bulanan", period: "", method: "Tunai", ref_type: "other", ref_id: "" });
  const { lang, t } = useLang();
  const load = () => {
    api.get("/tenant/shares").then((r) => setShares(r.data.transactions));
    api.get("/tenant/fees").then((r) => setFees(r.data.fees));
    api.get("/tenant/payments").then((r) => setPayments(r.data.payments));
    api.get("/tenant/members").then((r) => setMembers(r.data.members.filter((m) => m.status === "active")));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (modal === "share") {
        const res = await api.post(`/tenant/members/${form.member_id}/shares`, { type: form.type, amount: Number(form.amount), note: form.note });
        toast.success(res.status === "pending_approval" ? "Pengeluaran dihantar untuk kelulusan" : "Transaksi saham direkodkan");
      } else if (modal === "fee") {
        await api.post("/tenant/fees", { member_id: form.member_id, fee_type: form.fee_type, amount: Number(form.amount), period: form.period });
        toast.success("Yuran dicaj");
      } else {
        const res = await api.post("/tenant/payments", { member_id: form.member_id, amount: Number(form.amount), method: form.method, ref_type: form.ref_type, ref_id: form.ref_id });
        toast.success(`Resit ${res.data.receipt_no} dijana`);
      }
      setModal(null);
      setForm({ member_id: "", type: "purchase", amount: "", note: "", fee_type: "Yuran Bulanan", period: "", method: "Tunai", ref_type: "other", ref_id: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (!shares || !fees || !payments) return <Spinner />;

  const unpaidFees = fees.filter((f) => f.status === "unpaid" && (!form.member_id || f.member_id === form.member_id));

  return (
    <div data-testid="tenant-finance">
      <PageTitle title={lang === "bm" ? "Kewangan" : "Finance"} sub={lang === "bm" ? "Saham, yuran, bayaran & resit" : "Shares, fees, payments & receipts"}>
        <div className="flex gap-2">
          <button data-testid="add-share-btn" onClick={() => setModal("share")} className="inline-flex items-center gap-1.5 bg-brand-deep text-white text-xs font-semibold px-3.5 py-2.5 rounded-xl hover:bg-brand-dark transition-colors"><Plus className="w-3.5 h-3.5" /> Saham</button>
          <button data-testid="add-fee-btn" onClick={() => setModal("fee")} className="inline-flex items-center gap-1.5 bg-gold text-brand-dark text-xs font-bold px-3.5 py-2.5 rounded-xl hover:bg-gold-hover transition-colors"><Plus className="w-3.5 h-3.5" /> Yuran</button>
          <button data-testid="add-payment-btn" onClick={() => setModal("payment")} className="inline-flex items-center gap-1.5 bg-brand-vivid text-white text-xs font-semibold px-3.5 py-2.5 rounded-xl hover:bg-brand-deep transition-colors"><Plus className="w-3.5 h-3.5" /> Bayaran</button>
        </div>
      </PageTitle>

      <div className="flex gap-2 mb-4">
        {TABS.map((tb) => (
          <button key={tb.key} data-testid={`finance-tab-${tb.key}`} onClick={() => setTab(tb.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === tb.key ? "bg-brand-deep text-white" : "bg-card border border-brand-deep/15 text-brand-deep hover:bg-brand-light"}`}>
            {lang === "bm" ? tb.bm : tb.en}
          </button>
        ))}
      </div>

      <div className="bg-card rounded-xl border border-brand-deep/10 overflow-x-auto">
        {tab === "shares" && (
          <table className="w-full text-sm min-w-[680px]">
            <thead><tr className="border-b border-brand-deep/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3">{t("common.date")}</th><th className="px-5 py-3">Ahli</th><th className="px-5 py-3">Jenis</th><th className="px-5 py-3 text-right">{t("common.amount")}</th><th className="px-5 py-3">Nota</th>
            </tr></thead>
            <tbody>
              {shares.map((s) => (
                <tr key={s.txn_id} className="border-b border-brand-deep/5" data-testid={`share-row-${s.txn_id.slice(-6)}`}>
                  <td className="px-5 py-3 text-xs">{fmtDate(s.date)}</td>
                  <td className="px-5 py-3 font-medium text-brand-dark">{s.member_name}</td>
                  <td className="px-5 py-3"><span className={`text-xs font-bold uppercase ${s.type === "withdrawal" ? "text-red-500" : "text-brand-vivid"}`}>{s.type}</span></td>
                  <td className="px-5 py-3 text-right font-mono">{fmtRM(s.amount)}</td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{s.note || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "fees" && (
          <table className="w-full text-sm min-w-[680px]">
            <thead><tr className="border-b border-brand-deep/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3">Ahli</th><th className="px-5 py-3">Jenis</th><th className="px-5 py-3">Tempoh</th><th className="px-5 py-3 text-right">{t("common.amount")}</th><th className="px-5 py-3">{t("common.status")}</th><th className="px-5 py-3">Resit</th>
            </tr></thead>
            <tbody>
              {fees.map((f) => (
                <tr key={f.fee_id} className="border-b border-brand-deep/5" data-testid={`fee-row-${f.fee_id.slice(-6)}`}>
                  <td className="px-5 py-3 font-medium text-brand-dark">{f.member_name}</td>
                  <td className="px-5 py-3 text-xs">{f.fee_type}</td>
                  <td className="px-5 py-3 text-xs font-mono">{f.period || "-"}</td>
                  <td className="px-5 py-3 text-right font-mono">{fmtRM(f.amount)}</td>
                  <td className="px-5 py-3"><StatusBadge status={f.status} /></td>
                  <td className="px-5 py-3 text-xs font-mono">{f.receipt_no || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "payments" && (
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="border-b border-brand-deep/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3">No. Resit</th><th className="px-5 py-3">{t("common.date")}</th><th className="px-5 py-3">Ahli</th><th className="px-5 py-3">Kaedah</th><th className="px-5 py-3">Rujukan</th><th className="px-5 py-3 text-right">{t("common.amount")}</th>
            </tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.payment_id} className="border-b border-brand-deep/5" data-testid={`payment-row-${p.receipt_no}`}>
                  <td className="px-5 py-3 font-mono text-xs font-bold text-brand-deep">{p.receipt_no}</td>
                  <td className="px-5 py-3 text-xs">{fmtDate(p.paid_at)}</td>
                  <td className="px-5 py-3 font-medium text-brand-dark">{p.member_name}</td>
                  <td className="px-5 py-3 text-xs">{p.method}</td>
                  <td className="px-5 py-3 text-xs uppercase">{p.ref_type}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">{fmtRM(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModal(null)} />
          <form onSubmit={submit} className="relative bg-card rounded-2xl p-6 w-full max-w-md animate-fade-up" data-testid={`${modal}-form`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-brand-dark">
                {modal === "share" ? "Transaksi Saham" : modal === "fee" ? "Caj Yuran" : "Rekod Bayaran"}
              </h3>
              <button type="button" onClick={() => setModal(null)} className="text-muted-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <select data-testid={`${modal}-member-select`} required value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm bg-white">
                <option value="">Pilih Ahli *</option>
                {members.map((m) => <option key={m.member_id} value={m.member_id}>{m.member_no} — {m.name}</option>)}
              </select>
              {modal === "share" && (
                <select data-testid="share-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm bg-white">
                  <option value="purchase">Belian Saham</option>
                  <option value="dividend">Dividen</option>
                  <option value="withdrawal">Pengeluaran (perlu kelulusan)</option>
                </select>
              )}
              {modal === "fee" && (
                <>
                  <select value={form.fee_type} onChange={(e) => setForm({ ...form, fee_type: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm bg-white">
                    {["Yuran Bulanan", "Yuran Tahunan", "Yuran Masuk", "Denda"].map((f) => <option key={f}>{f}</option>)}
                  </select>
                  <input placeholder="Tempoh cth: 2026-06" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm" />
                </>
              )}
              {modal === "payment" && (
                <>
                  <select data-testid="payment-method-select" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm bg-white">
                    {["Tunai", "FPX", "Bank Transfer", "Cek", "Potongan Gaji"].map((m) => <option key={m}>{m}</option>)}
                  </select>
                  <select value={form.ref_type} onChange={(e) => setForm({ ...form, ref_type: e.target.value, ref_id: "" })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm bg-white">
                    <option value="other">Lain-lain</option>
                    <option value="fee">Bayar Yuran</option>
                    <option value="share">Tambah Saham</option>
                  </select>
                  {form.ref_type === "fee" && (
                    <select data-testid="payment-fee-select" value={form.ref_id} onChange={(e) => setForm({ ...form, ref_id: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm bg-white">
                      <option value="">Pilih Yuran Tertunggak</option>
                      {unpaidFees.map((f) => <option key={f.fee_id} value={f.fee_id}>{f.fee_type} {f.period} — {fmtRM(f.amount)}</option>)}
                    </select>
                  )}
                </>
              )}
              <input data-testid={`${modal}-amount-input`} required type="number" step="0.01" min="0.01" placeholder="Amaun (RM) *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm font-mono" />
              {modal === "share" && <input placeholder="Nota" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm" />}
            </div>
            <button data-testid={`${modal}-submit-btn`} type="submit" className="mt-4 w-full bg-brand-deep text-white rounded-xl py-2.5 text-sm font-bold hover:bg-brand-dark transition-colors">Simpan</button>
          </form>
        </div>
      )}
    </div>
  );
}
